// How one symbol's drawings stay the same in every chart that shows them: a
// pane's edit is merged into the shared list by id, and a pane applies a new
// shared list by changing only what changed. Pure (drawings are immutable: the
// drawing library replaces a drawing's object on every edit), so it runs and is
// tested anywhere.

export interface Identified {
  readonly id: string
}

/**
 * A pane's edit — its list went from `before` to `after` — applied to the
 * shared list `base`.
 *
 * - Up to date (`base` is `before`): `after` itself.
 * - Otherwise by id: drawings the pane removed are removed; drawings removed
 *   elsewhere stay removed (a remote delete wins over an edit of it); drawings the
 *   pane changed (a new object) replace theirs; drawings it added are appended;
 *   everything else in `base` (other panes, the server's copy) is kept.
 * - An edit that changes nothing returns `base`.
 */
export const rebase = <T extends Identified>(
  base: ReadonlyArray<T>,
  before: ReadonlyArray<T>,
  after: ReadonlyArray<T>
): ReadonlyArray<T> => {
  if (base === before) return after
  const beforeById = new Map(before.map((d) => [d.id, d]))
  const afterById = new Map(after.map((d) => [d.id, d]))
  const removed = new Set(before.filter((d) => !afterById.has(d.id)).map((d) => d.id))
  const changed = after.filter((d) => beforeById.has(d.id) && beforeById.get(d.id) !== d)
  const added = after.filter((d) => !beforeById.has(d.id))
  if (removed.size === 0 && changed.length === 0 && added.length === 0) return base
  const changedById = new Map(changed.map((d) => [d.id, d]))
  const inBase = new Set(base.map((d) => d.id))
  const merged = base.flatMap((d) => (removed.has(d.id) ? [] : [changedById.get(d.id) ?? d]))
  return [...merged, ...added.filter((d) => !inBase.has(d.id))]
}

export type ApplyPlan<T> =
  | { readonly kind: "none" }
  /** Same drawings in the same order: update these in place (selection and edits in progress survive). */
  | { readonly kind: "update"; readonly changed: ReadonlyArray<T> }
  /** Drawings added, removed, or reordered: load the whole list. */
  | { readonly kind: "replace" }

export const planApply = <T extends Identified>(current: ReadonlyArray<T>, next: ReadonlyArray<T>): ApplyPlan<T> => {
  if (current === next) return { kind: "none" }
  if (current.length !== next.length || current.some((d, i) => d.id !== next[i]!.id)) return { kind: "replace" }
  const changed = next.filter((d, i) => d !== current[i])
  return changed.length === 0 ? { kind: "none" } : { kind: "update", changed }
}
